import { useState, useEffect } from "react";
import { ZodSchema } from "zod";
import { useResetPasswordMutation } from "./useResetPasswordMutation";
import { baseResetPasswordSchema } from "../auth.schemas";

export type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

export type UseResetPasswordProps = {
  /** OOB code from password reset email */
  oobCode: string;
  /** Success callback */
  onSuccess?: (data: any) => void;
  /** Error callback */
  onError?: () => void;
  /** URL to redirect to after success */
  successRedirectUrl?: string;
  /** Custom schema (defaults to baseResetPasswordSchema) */
  schema?: ZodSchema;
};

export type UseResetPasswordReturn = {
  /** Reset password mutation for form submission */
  resetPasswordMutation: ReturnType<typeof useResetPasswordMutation>;
  /** Whether the password was reset successfully */
  isSuccess: boolean;
  /** Schema for form validation */
  schema: ZodSchema;
  /** Default values for form */
  defaultValues: ResetPasswordFormData;
};

/**
 * Composite hook for reset password functionality.
 * Manages mutation, success state, and optional redirect.
 *
 * Usage:
 * ```tsx
 * const {
 *   resetPasswordMutation,
 *   isSuccess,
 *   schema,
 *   defaultValues,
 * } = useResetPassword({
 *   oobCode: searchParams.get('oobCode') || '',
 *   onSuccess: () => console.log('Password reset'),
 *   successRedirectUrl: 'https://example.com', // optional
 * });
 *
 * const { form, handleSubmit } = useForm({
 *   schema,
 *   onSubmit: resetPasswordMutation.mutateAsync,
 *   defaultValues,
 * });
 * ```
 */
export function useResetPassword({
  oobCode,
  onSuccess,
  onError,
  successRedirectUrl,
  schema = baseResetPasswordSchema,
}: UseResetPasswordProps): UseResetPasswordReturn {
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset password mutation with success tracking
  const resetPasswordMutation = useResetPasswordMutation({
    oobCode,
    onSuccess: (data: any) => {
      setIsSuccess(true);
      onSuccess?.(data);
    },
    onError,
    schema,
  });

  // Handle redirect on success
  useEffect(() => {
    if (isSuccess && successRedirectUrl) {
      const timer = setTimeout(() => {
        window.location.href = successRedirectUrl;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, successRedirectUrl]);

  const defaultValues: ResetPasswordFormData = {
    password: "",
    confirmPassword: "",
  };

  return {
    resetPasswordMutation,
    isSuccess,
    schema,
    defaultValues,
  };
}

export default useResetPassword;

